import { isLiveMarketingHost } from '@/lib/tracking/marketing-tags';

import {
  ADSENSE_CLIENT_ID,
  MOBILE_BREAKPOINT_PX,
  PLACEMENT_DEFINITIONS,
  type AdPlacementId,
  type AdSettingsFlags,
} from './placements';

export type ResolveAdSlotInput = {
  placement: AdPlacementId;
  flags: AdSettingsFlags;
  isPaidUser: boolean;
  /** Viewport width in CSS pixels. */
  viewportWidth: number;
  hostname: string;
};

export type ResolvedAdSlot =
  | { kind: 'none' }
  | {
      kind: 'live';
      slotId: string;
      clientId: string;
      format: 'responsive';
    }
  | {
      kind: 'placeholder';
      slotId: string;
      format: 'responsive';
    };

/**
 * Pure gate for AdContainer. Eligible only when master is yes, placement is yes,
 * user is unpaid, and (desktop, or mobile allowed for that placement and
 * ads_mobile_enabled is yes). Live host → real unit; otherwise placeholder.
 */
export function resolveAdSlot(input: ResolveAdSlotInput): ResolvedAdSlot {
  const { placement, flags, isPaidUser, viewportWidth, hostname } = input;

  if (isPaidUser) {
    return { kind: 'none' };
  }
  if (flags.ads_enabled !== 'yes') {
    return { kind: 'none' };
  }

  const def = PLACEMENT_DEFINITIONS[placement];
  if (!def) {
    return { kind: 'none' };
  }
  if (flags[def.flagKey] !== 'yes') {
    return { kind: 'none' };
  }

  const isMobile = viewportWidth < MOBILE_BREAKPOINT_PX;
  if (isMobile) {
    if (!def.mobileAllowed) {
      return { kind: 'none' };
    }
    if (flags.ads_mobile_enabled !== 'yes') {
      return { kind: 'none' };
    }
  }

  if (isLiveMarketingHost(hostname)) {
    return {
      kind: 'live',
      slotId: def.slotId,
      clientId: ADSENSE_CLIENT_ID,
      format: 'responsive',
    };
  }

  return {
    kind: 'placeholder',
    slotId: def.slotId,
    format: 'responsive',
  };
}
